'use client'

import type { UseProductViewTrackingOptions } from '@maestro/commerce-blocks/hooks/hooks.types'
import type { Product } from '@maestro/data-layer'

import { useEffect, useRef } from 'react'

import { VIEW_EVENT_DELAY } from '@maestro/commerce-blocks/hooks/use-visibility-tracked-event'

const scheduleIdle = (cb: () => void): number =>
  typeof requestIdleCallback === 'function'
    ? requestIdleCallback(cb)
    : (setTimeout(cb, 1) as unknown as number)

const cancelIdle = (id: number): void => {
  if (typeof cancelIdleCallback === 'function') {
    cancelIdleCallback(id)
  } else {
    clearTimeout(id)
  }
}

/**
 * Observes individual `[data-product-id]` elements within a container,
 * batches the visible ones after a debounce, and calls `onPublish`
 * with the products that were actually seen.
 */
export function useProductViewTracking(props: UseProductViewTrackingOptions) {
  const { containerRef, products, onPublish, enabled = true } = props
  const trackedIdsRef = useRef<Set<string>>(new Set())
  const pendingIdsRef = useRef<Set<string>>(new Set())
  const timerRef = useRef<NodeJS.Timeout | null>(null)
  const observerRef = useRef<IntersectionObserver | null>(null)
  const onPublishRef = useRef(onPublish)

  useEffect(() => {
    onPublishRef.current = onPublish
  }, [onPublish])

  // Reset tracked products when product list changes completely
  useEffect(() => {
    const currentIds = new Set(products.map((p) => String(p.id)))
    const hasOverlap = Array.from(trackedIdsRef.current).some((id) =>
      currentIds.has(id)
    )

    if (!hasOverlap && trackedIdsRef.current.size > 0) {
      trackedIdsRef.current.clear()
      pendingIdsRef.current.clear()
    }
  }, [products])

  useEffect(() => {
    if (!enabled || !products.length) {
      return
    }

    if (observerRef.current) {
      observerRef.current.disconnect()
    }

    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const productId = (entry.target as HTMLElement).dataset.productId
            if (productId && !trackedIdsRef.current.has(productId)) {
              pendingIdsRef.current.add(productId)
              trackedIdsRef.current.add(productId)
            }
          }
        })

        if (pendingIdsRef.current.size > 0) {
          if (timerRef.current) {
            clearTimeout(timerRef.current)
          }

          timerRef.current = setTimeout(() => {
            const productIds = Array.from(pendingIdsRef.current)
            const visibleProducts = productIds
              .map((id) => {
                const idx = products.findIndex((p) => String(p.id) === id)
                return idx >= 0
                  ? { product: products[idx], position: idx }
                  : null
              })
              .filter(
                (item): item is { product: Product; position: number } =>
                  item !== null
              )

            if (visibleProducts.length > 0) {
              onPublishRef.current(visibleProducts)
            }

            pendingIdsRef.current.clear()
          }, VIEW_EVENT_DELAY)
        }
      },
      { threshold: 0.5 }
    )

    const container = containerRef.current
    // Defer attaching observers off the mount commit: the display:contents
    // check below calls getComputedStyle per item, forcing a reflow. View
    // tracking is non-critical, so run it during idle time.
    let idleId: number | undefined
    if (container) {
      idleId = scheduleIdle(() => {
        const items = container.querySelectorAll('[data-product-id]')
        items.forEach((item) => {
          // Observe the first element child if it has display: contents
          const targetToObserve =
            getComputedStyle(item).display === 'contents' &&
            item.firstElementChild
              ? item.firstElementChild
              : item

          // Keep the product ID so we can resolve it in the callback
          if (targetToObserve !== item) {
            ;(targetToObserve as HTMLElement).dataset.productId = (
              item as HTMLElement
            ).dataset.productId
          }

          observerRef.current?.observe(targetToObserve)
        })
      })
    }

    return () => {
      if (idleId !== undefined) {
        cancelIdle(idleId)
      }
      if (observerRef.current) {
        observerRef.current.disconnect()
      }
      if (timerRef.current) {
        clearTimeout(timerRef.current)
      }
    }
  }, [products, enabled, containerRef])
}
